/*
 * Назначение: настройки защиты входа на странице «Безопасность».
 * Функционал: пороги и пауза при подборе пароля, письма-тревоги, параметры
 * кода подтверждения и срок доверия устройству. Значения приходят с сервера и
 * там же подрезаются по допустимым границам — в интерфейсе только подсказки.
 */

import { useState } from "react";
import { Loader2, Save } from "lucide-react";

import type { SecuritySettings } from "../../../lib/security-api";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";

/** Числовое поле с подписью и пояснением. */
function NumberField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="text-xs text-muted-foreground">
      {label}
      <Input
        type="number"
        className="mt-1 w-40"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="mt-1 block">{hint}</span>
    </label>
  );
}

/** Переключатель с пояснением. */
function ToggleRow({
  title,
  hint,
  checked,
  onChange,
}: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border p-3">
      <div className="pr-4">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function SecuritySettingsCard({
  settings,
  saving,
  onSave,
}: {
  settings: SecuritySettings;
  saving: boolean;
  onSave: (patch: SecuritySettings) => void;
}) {
  const [form, setForm] = useState<SecuritySettings>(settings);
  const set = (patch: Partial<SecuritySettings>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Защита входа</CardTitle>
        <CardDescription>
          После нескольких неверных попыток вход приостанавливается, а при
          упорном подборе пауза удлиняется. Подтверждение кодом добавляет второй
          рубеж: пароля уже недостаточно.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-4">
          <NumberField
            label="Неудач по логину"
            hint="после этого пауза"
            value={form.login_max_failures}
            onChange={(login_max_failures) => set({ login_max_failures })}
          />
          <NumberField
            label="Неудач с одного адреса"
            hint="считаем отдельно от логина"
            value={form.ip_max_failures}
            onChange={(ip_max_failures) => set({ ip_max_failures })}
          />
          <NumberField
            label="Окно подсчёта, мин"
            hint="за сколько времени считаем"
            value={form.window_minutes}
            onChange={(window_minutes) => set({ window_minutes })}
          />
          <NumberField
            label="Пауза, мин"
            hint="растёт при новых попытках"
            value={form.block_minutes}
            onChange={(block_minutes) => set({ block_minutes })}
          />
          <NumberField
            label="Хранить журнал, дней"
            hint="старые записи удаляются ночью"
            value={form.attempts_retention_days}
            onChange={(attempts_retention_days) => set({ attempts_retention_days })}
          />
        </div>

        <ToggleRow
          title="Письмо при подборе пароля"
          hint="Одно письмо на серию попыток, без спама."
          checked={form.notify_on_attack}
          onChange={(notify_on_attack) => set({ notify_on_attack })}
        />
        <ToggleRow
          title="Письмо при входе с нового адреса"
          hint="Приходит, если с этого адреса раньше не входили."
          checked={form.notify_on_new_ip}
          onChange={(notify_on_new_ip) => set({ notify_on_new_ip })}
        />

        <label className="block text-xs text-muted-foreground">
          Адрес для писем безопасности и кодов
          <Input
            className="mt-1"
            placeholder="пусто — берём адрес из настроек почты"
            value={form.security_email}
            onChange={(e) => set({ security_email: e.target.value })}
          />
        </label>

        <ToggleRow
          title="Подтверждение входа кодом с почты"
          hint="Общий выключатель. Дополнительно включается у пользователя ниже."
          checked={form.twofa_enabled}
          onChange={(twofa_enabled) => set({ twofa_enabled })}
        />

        <div className="flex flex-wrap gap-4">
          <NumberField
            label="Длина кода"
            hint="от 4 до 8 цифр"
            value={form.code_length}
            onChange={(code_length) => set({ code_length })}
          />
          <NumberField
            label="Код живёт, мин"
            hint="потом нужен новый"
            value={form.code_ttl_minutes}
            onChange={(code_ttl_minutes) => set({ code_ttl_minutes })}
          />
          <NumberField
            label="Попыток ввода кода"
            hint="дальше код сгорает"
            value={form.code_max_attempts}
            onChange={(code_max_attempts) => set({ code_max_attempts })}
          />
          <NumberField
            label="Помнить устройство, дней"
            hint="0 — код при каждом входе"
            value={form.trusted_device_days}
            onChange={(trusted_device_days) => set({ trusted_device_days })}
          />
        </div>

        <Button onClick={() => onSave(form)} disabled={saving}>
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Сохранить настройки
        </Button>
      </CardContent>
    </Card>
  );
}
